import * as React from "react";
import { SortDirectionType } from "react-virtualized";
import { FilterConfig } from "../ScrollableTable";
import { FiltersDropdown } from "./FiltersDropdown";
import classnames from "classnames";
import { CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";
import { CustomFilter } from "./CustomFilter";
import { Resizable } from "react-resizable";
import type { ResizeCallbackData } from "react-resizable";

const SortIndicator = ({ sortDirection }: { sortDirection: SortDirectionType }) => {
  return (
    <div className="sort-icon">
      {sortDirection === "ASC" ? (
        <>
          <CaretUpOutlined className="on" />
          <CaretDownOutlined />
        </>
      ) : (
        <>
          <CaretUpOutlined />
          <CaretDownOutlined className="on" />
        </>
      )}
    </div>
  );
};

export interface CustomFilterRenderProps {
  onFilter: ScrollableTableHeaderProps["onFilter"];
  filters?: ScrollableTableHeaderProps["filters"];
  selectedFilters?: ScrollableTableHeaderProps["selectedFilters"];
  confirm: () => void;
  clearFilters: () => void;
  setFilters: (filters: string[] | number[]) => void;
}

interface ScrollableTableHeaderProps {
  dataKey: string;
  label: React.ReactNode | undefined;
  sortBy: string;
  sortDirection: SortDirectionType;
  filters?: FilterConfig[];
  selectedFilters?: string[] | number[];
  onFilter?: (filters: string[] | number[]) => void;
  disableSort?: boolean;
  singleFilter?: boolean;
  filterDropdownMinWidth?: number;
  customFilterRender?: (renderProps: CustomFilterRenderProps) => React.ReactNode;
  onResize?: (columnIndex: number, width: number) => void;
  handleDoubleClickResize?: (e: React.MouseEvent, columnIndex: number) => void;
  width?: number;
  index?: number;
  ignoreResizeableHeaders?: string[]; // pass datakey from columns to disable resize feature for selected column
  /** Minimum width when resizing (also passed to react-resizable minConstraints). */
  minResizeWidth?: number;
  headerDataTestId?: string;
  filterIconDataTestId?: string;
}

export const ScrollableTableHeader: React.FunctionComponent<ScrollableTableHeaderProps> = ({
  dataKey,
  label,
  sortBy,
  sortDirection,
  filters,
  selectedFilters,
  onFilter,
  disableSort,
  singleFilter,
  filterDropdownMinWidth,
  customFilterRender,
  onResize,
  handleDoubleClickResize,
  width,
  index,
  ignoreResizeableHeaders,
  minResizeWidth = 48,
  headerDataTestId,
  filterIconDataTestId,
}) => {
  const showSortIndicator = sortBy === dataKey;
  /** Match any column that shows a filter control (dropdown, custom search, etc.) */
  const hasFilter = Boolean(filters?.length || customFilterRender);
  const clName = classnames("sort-item", { "no-sort": disableSort, "has-filter": hasFilter });

  let filterComponent: React.ReactNode = null;
  if (customFilterRender) {
    filterComponent = (
      <CustomFilter
        onFilter={onFilter}
        filters={filters}
        selectedFilters={selectedFilters}
        renderFunction={customFilterRender}
        filterIconDataTestId={filterIconDataTestId}
      />
    );
  } else if (filters) {
    filterComponent = (
      <FiltersDropdown
        columnDataKey={String(dataKey)}
        dropdownMinWidth={filterDropdownMinWidth}
        singleFilter={singleFilter}
        filters={filters}
        selectedFilters={selectedFilters}
        onFilter={onFilter}
        filterIconDataTestId={filterIconDataTestId}
      />
    );
  }
  const columnLayout = (
    <div className={clName} data-testid={headerDataTestId} title={typeof label === "string" ? label : null}>
      <div className="header-label">{label}</div>
      {showSortIndicator && <SortIndicator sortDirection={sortDirection} />}
      {filterComponent}
    </div>
  );
  if (ignoreResizeableHeaders?.includes(dataKey)) {
    return columnLayout;
  }
  if (onResize && width) {
    const handleResize = (e: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      e.preventDefault();
      e.stopPropagation();
      onResize(index, size.width);
    };
    return (
      <Resizable
        width={width}
        height={0}
        axis="x"
        resizeHandles={["e"]}
        handleSize={[12, 40]}
        lockAspectRatio={false}
        minConstraints={[minResizeWidth, 0]}
        maxConstraints={[Infinity, Infinity]}
        transformScale={1}
        handle={
          <span
            aria-colindex={index + 1}
            className="react-resizable-handle"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDoubleClick={(e) => handleDoubleClickResize(e, index)}
          />
        }
        onResize={handleResize}
        draggableOpts={{ enableUserSelectHack: false }}
      >
        {columnLayout}
      </Resizable>
    );
  }

  return columnLayout;
};
